import ImageIcon from '@mui/icons-material/Collections';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { Imagen } from '@/tipos';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface Props {
  imagenes: Imagen[];
  /** Novedades pasa solo; ofertas se mueve cuando la persona quiere. Es como estaba. */
  automatico?: boolean;
  vacio: string;
}

/**
 * El carrusel de novedades y el de ofertas.
 *
 * Eran dos componentes casi idénticos. La diferencia real entre ellos es si avanza solo, y eso
 * ahora es un parámetro.
 */
export function CarruselDeImagenes({ imagenes, automatico = false, vacio }: Props) {
  if (imagenes.length === 0) {
    return (
      // Antes eran 400px de alto con una línea de texto flotando al medio: un agujero enorme
      // que parecía que algo no había cargado. Una caja punteada, del alto que necesita el
      // mensaje, dice lo mismo y se lee como una decisión en vez de como un error.
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 1.5,
          my: 3,
          py: 5,
          px: 3,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'rgba(255,255,255,.12)',
          bgcolor: 'rgba(255,255,255,.02)',
        }}
      >
        <ImageIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
        <Typography variant="body1" color="text.secondary">
          {vacio}
        </Typography>
      </Box>
    );
  }

  return (
    // Las flechas y el punto del paginador venían del azul de fábrica de swiper (#007aff), que
    // no aparece en ninguna otra parte del sitio. `--swiper-theme-color` es la variable que el
    // propio swiper lee, así que alcanza con pisarla acá en vez de perseguir cada clase.
    <Box
      sx={theme => ({
        my: 4,
        '--swiper-theme-color': theme.palette.primary.main,
        '--swiper-navigation-size': '32px',
        // Las flechas quedaban pegadas a los bordes de la pantalla, lejísimos de la imagen.
        '& .swiper-button-next, & .swiper-button-prev': {
          transition: 'opacity .25s ease',
          opacity: 0.65,
          '&:hover': { opacity: 1 },
        },
      })}
    >
      <Swiper
        modules={automatico ? [Navigation, Pagination, Autoplay] : [Navigation, Pagination]}
        navigation
        pagination={{ clickable: true }}
        spaceBetween={20}
        slidesPerView={1}
        loop={imagenes.length > 1}
        autoplay={automatico ? { delay: 5000, disableOnInteraction: false } : false}
      >
        {imagenes.map(imagen => {
          // El código del producto sale del nombre del archivo, hasta el primer guion bajo:
          // "CK1234_frente" busca CK1234. Es la convención con la que se cargan las imágenes.
          const codigo = imagen.name.split('_')[0] || imagen.name;

          return (
            <SwiperSlide key={imagen.id}>
              <Link
                to={`/catalogo?search=${encodeURIComponent(codigo)}&page=1`}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 400, md: 600 },
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.3s ease' },
                  }}
                >
                  <Box
                    component="img"
                    src={imagen.url}
                    alt={imagen.alt_text ?? imagen.name}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
}
